import { useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import { useOutletContext } from "react-router-dom";
import { apiCall } from "../../lib/api-client.js";
import {
  ApiError,
  type BootstrapPayload,
} from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import type { OrgDetailContext } from "./context.js";
import { InlineLoading } from "../../components/Spinner.js";
import { ErrorBanner, InfoBanner } from "../../components/ErrorBanner.js";

function CountCard({
  label,
  count,
  note,
}: {
  label: string;
  count: number;
  note?: string;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-dosia-border bg-dosia-bg-light p-4">
      <p className="text-sm text-dosia-text-secondary">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-dosia-text">{count}</p>
      {note ? <p className="mt-1 text-xs text-dosia-text-dim">{note}</p> : null}
    </div>
  );
}

export function BootstrapPreview(): JSX.Element {
  const { org } = useOutletContext<OrgDetailContext>();
  const auth = useAtomValue(authAtom);
  const [data, setData] = useState<BootstrapPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!auth) return;
    void (async () => {
      try {
        const payload = await apiCall<BootstrapPayload>(
          "GET",
          `/api/organizations/${org.code}/bootstrap`,
          { token: auth.token },
        );
        if (!cancelled) {
          setData(payload);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof ApiError ? err.message : "加载失败";
        setError(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth, org.code]);

  return (
    <div className="space-y-4">
      <InfoBanner>
        <strong>Phase D 等待中：</strong>
        当前端点返回 mock 空集合，dosia desktop 接入后 ({" "}
        <code className="font-mono text-xs">Phase D</code>) 会读取真实 roster /
        roles / 审批人映射并下发到企业客户端。
      </InfoBanner>

      {error ? <ErrorBanner onDismiss={() => setError(null)}>{error}</ErrorBanner> : null}

      {!data && !error ? <InlineLoading label="加载 bootstrap…" /> : null}

      {data ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <CountCard
              label="员工（employees）"
              count={data.employees.length}
              note="Phase D 接入后由后端按 orgId 计算"
            />
            <CountCard
              label="角色（roles）"
              count={data.roles.length}
              note="C.2 加 CRUD 后由 admin 在网站维护"
            />
            <CountCard
              label="审批人映射键数"
              count={Object.keys(data.leadershipMap).length}
              note="leadershipMap[roleCode] = [用户名]"
            />
          </div>

          <div className="rounded-lg border border-dosia-border bg-dosia-bg-light p-4 text-sm text-dosia-text-secondary">
            <p className="font-medium text-dosia-text mb-1">org 字段</p>
            <p>code: <span className="font-mono">{data.org.code}</span></p>
            <p>name: {data.org.name}</p>
            {data.org.dosiaProvisioning?.bytespikeOrgCode ? (
              <p>
                bytespikeOrgCode:{" "}
                <span className="font-mono">
                  {data.org.dosiaProvisioning.bytespikeOrgCode}
                </span>
              </p>
            ) : null}
            {data.etag ? (
              <p className="mt-1 text-xs text-dosia-text-dim">
                ETag: <span className="font-mono">{data.etag}</span>
              </p>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
